import { useEffect, useState } from 'react';

import { useAuth } from '@auth/AuthProvider';
import SeriesController from '@data/controller/SeriesController';
import usePagedList from '@hooks/usePagedList';
import { StaffAction } from '@interfaces/church.interfaces';
import { ChurchSeries } from '@interfaces/series.interface';
import { ChurchService } from '@interfaces/service.interface';

const HTTP_NOT_FOUND = 404;

// A campus's series in one category, a page at a time, newest start date
// first.
// Nothing is fetched until a campus is chosen. A null category means the
// campus's uncategorized series.
export function useSeriesList(campusID: string | null, categoryID: string | null) {
  const auth = useAuth();
  const churchID = auth.churchId;

  const key = churchID && campusID ? `${campusID}|${categoryID ?? ''}` : null;

  const list = usePagedList<ChurchSeries>(key, async (cursor) => {
    const response = await SeriesController.fetchSeriesPage(churchID!, campusID!, categoryID, cursor, auth);
    const body = response.body;

    if (response.error || !body) {
      return { error: response.error || 'Failed to load series.', status: response.status };
    }

    return { items: body.results ?? [], hasMore: body.hasMore === true, next: body.nextLastSeriesID ?? null };
  });

  return { ...list, series: list.items } as const;
}

// A series and its services, a page at a time, newest first. Its campus is
// needed to find it: Sanctuary scopes a series to the campus it's filed in.
// `notFound` separates a series that isn't there from a failed request.
export function useSeriesBundle(campusID: string | null, seriesID: string | null) {
  const auth = useAuth();
  const churchID = auth.churchId;
  // Staff who manage services also see the series' upcoming services.
  const includeUpcoming = auth.hasPermission(StaffAction.MANAGE_SERVICES);

  const key = churchID && campusID && seriesID ? `${campusID}|${seriesID}|${includeUpcoming}` : null;

  const list = usePagedList<ChurchService, ChurchSeries>(key, async (cursor) => {
    const response = await SeriesController.fetchSeries(churchID!, campusID!, seriesID!, includeUpcoming, cursor, auth);
    const body = response.body;

    if (response.error || !body) {
      return { error: response.error || 'Failed to load this series.', status: response.status };
    }

    return {
      items: body.services?.results ?? [],
      hasMore: body.services?.hasMore === true,
      next: body.services?.nextLastServiceID ?? null,
      extra: body.series
    };
  });

  return {
    ...list,
    series: list.extra,
    services: list.items,
    notFound: list.errorStatus === HTTP_NOT_FOUND
  } as const;
}

// Just a series itself, for showing which one something belongs to. Null
// until it's loaded, and stays null if it can't be found at that campus.
//
// The answer is kept with the series it's for, so the render after the IDs
// change already reads as loading rather than briefly as "not found".
export function useSeriesSummary(campusID: string | null, seriesID: string | null) {
  const auth = useAuth();
  const churchID = auth.churchId;

  const key = churchID && campusID && seriesID ? `${campusID}|${seriesID}` : null;
  const [loaded, setLoaded] = useState<{ key: string; series: ChurchSeries | null } | null>(null);

  useEffect(() => {
    if (!key) return;

    let isCancelled = false;

    void SeriesController.fetchSeries(churchID!, campusID!, seriesID!, false, null, auth).then((response) => {
      if (isCancelled) return;

      setLoaded({ key, series: response.body?.series ?? null });
    });

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, auth]);

  const isCurrent = key !== null && loaded?.key === key;

  return {
    series: isCurrent ? loaded!.series : null,
    loading: key !== null && !isCurrent
  } as const;
}
